import {
  isAtomCommandInterrupted,
  type AtomCommandResult,
} from "@afar-research/client-runtime/state/runtime";
import type { ScopedThreadRef } from "@afar-research/contracts";

export interface TerminalProcessCloseRequest {
  readonly close: () => Promise<AtomCommandResult<unknown, unknown>>;
  readonly writeExit: () => Promise<AtomCommandResult<unknown, unknown>>;
}

interface TerminalCloseCommandInput {
  readonly environmentId: ScopedThreadRef["environmentId"];
  readonly input: {
    readonly threadId: ScopedThreadRef["threadId"];
    readonly terminalId: string;
    readonly deleteHistory: true;
  };
}

interface TerminalWriteCommandInput {
  readonly environmentId: ScopedThreadRef["environmentId"];
  readonly input: {
    readonly threadId: ScopedThreadRef["threadId"];
    readonly terminalId: string;
    readonly data: "exit\n";
  };
}

export interface ScopedTerminalProcessCloseRequest {
  readonly threadRef: ScopedThreadRef;
  readonly terminalId: string;
  readonly close: (
    input: TerminalCloseCommandInput,
  ) => Promise<AtomCommandResult<unknown, unknown>>;
  readonly write: (
    input: TerminalWriteCommandInput,
  ) => Promise<AtomCommandResult<unknown, unknown>>;
}

/**
 * Request explicit Environment-owned process termination, falling back to the
 * shell's exit command only when the close command deterministically fails.
 */
export async function requestTerminalProcessClose({
  close,
  writeExit,
}: TerminalProcessCloseRequest): Promise<void> {
  const closeResult = await close();
  if (closeResult._tag === "Failure" && !isAtomCommandInterrupted(closeResult)) {
    await writeExit();
  }
}

/** Build the authoritative close and fallback payloads for one scoped terminal. */
export function requestScopedTerminalProcessClose({
  threadRef,
  terminalId,
  close,
  write,
}: ScopedTerminalProcessCloseRequest): Promise<void> {
  return requestTerminalProcessClose({
    close: () =>
      close({
        environmentId: threadRef.environmentId,
        input: { threadId: threadRef.threadId, terminalId, deleteHistory: true },
      }),
    writeExit: () =>
      write({
        environmentId: threadRef.environmentId,
        input: { threadId: threadRef.threadId, terminalId, data: "exit\n" },
      }),
  });
}
